import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast, ToastContainer } from "react-toastify";
import PulseLoader from "react-spinners/PulseLoader";
import {
  Compass,
  UploadCloud,
  Trash2,
  Film,
  Image as ImageIcon,
  Calendar,
  Sparkles,
  Clock
} from "lucide-react";
import {
  createDiscover,
  getAllEventByOrganizer,
  getDiscoverByReference,
  deleteDiscover,
} from "../../../api";
import ConfirmationDialog from "../../../components/common/ConfirmationDialog";
import Background from "../../Login/Background";
import PageHeader from "../../../components/common/PageHeader/PageHeader";
import CustomSelect from "../../../components/common/CustomSelect/CustomSelect";
import { useNightMode } from "../../../contexts/NightModeContext";

interface EventOption {
  eventId: number;
  eventName: string;
  slugId?: string;
}

interface ImageItem {
  file: File;
  preview: string;
  caption: string;
}

interface DiscoverItem {
  id: number;
  fileUrl: string;
  eventImage?: string;
  eventName?: string;
  referenceId: number;
  referenceType: string;
  fileType?: string;
  createdAt?: string;
}

const PAGE_LIMIT = 12;

const DiscoverPage: React.FC = () => {
  const { isNightMode } = useNightMode();
  const [events, setEvents] = useState<EventOption[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<number | "">("");
  const [images, setImages] = useState<ImageItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
  const [existingItems, setExistingItems] = useState<DiscoverItem[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const [confirmationDialog, setConfirmationDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    type: "warning" as "warning" | "danger" | "info" | "success",
  });

  const closeConfirmationDialog = () => {
    setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    const fetchEvents = async () => {
      setEventsLoading(true);
      try {
        const data = await getAllEventByOrganizer();
        const list = Array.isArray(data) ? data : data?.data ?? [];
        setEvents(
          list.map((item: any) => ({
            eventId: item.eventId,
            eventName: item.eventName,
            slugId: item.slugId,
          }))
        );
      } catch (err: any) {
        toast.error("Failed to load events. Please try again.");
      } finally {
        setEventsLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const fetchDiscovers = useCallback(async (eventId: number, page: number) => {
    if (page === 1) {
      setPreviewLoading(true);
      setExistingItems([]);
    } else {
      setLoadingMore(true);
    }
    try {
      const data = await getDiscoverByReference(eventId, "Event", page, PAGE_LIMIT);
      const items: DiscoverItem[] = Array.isArray(data) ? data : data?.data ?? [];
      if (page === 1) {
        setExistingItems(items);
      } else {
        setExistingItems((prev) => [...prev, ...items]);
      }
      const pag = data?.pagination;
      setHasMore(pag?.hasNext ?? false);
      setTotalCount(pag?.total ?? 0);
      setCurrentPage(page);
    } catch {
      if (page === 1) setExistingItems([]);
    } finally {
      setPreviewLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !loadingMore &&
          !previewLoading &&
          selectedEventId !== ""
        ) {
          fetchDiscovers(selectedEventId as number, currentPage + 1);
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, previewLoading, selectedEventId, currentPage, fetchDiscovers]);

  const handleSelectEvent = (valStr: string) => {
    const val = valStr === "" ? "" : Number(valStr);
    setSelectedEventId(val);
    setCurrentPage(1);
    setHasMore(false);
    setTotalCount(0);
    if (val !== "") {
      fetchDiscovers(val as number, 1);
    } else {
      setExistingItems([]);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newItems: ImageItem[] = acceptedFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      caption: "",
    }));
    setImages((prev) => [...prev, ...newItems]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "image/gif": [],
      "image/webp": [],
      "video/mp4": [],
      "video/quicktime": [],
    },
    multiple: true,
  });

  const updateCaption = (index: number, value: string) => {
    setImages((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], caption: value };
      return updated;
    });
  };

  const removeImage = (index: number) => {
    setImages((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const removeExistingItem = (item: DiscoverItem) => {
    setConfirmationDialog({
      isOpen: true,
      title: "Delete Moment",
      message: "Are you sure you want to remove this discover moment?",
      type: "danger",
      onConfirm: async () => {
        try {
          await deleteDiscover(item.id);
          setExistingItems((prev) => prev.filter((i) => i.id !== item.id));
          setTotalCount((prev) => Math.max(0, prev - 1));
          toast.success("Moment deleted successfully.");
        } catch {
          toast.error("Failed to delete moment.");
        }
        closeConfirmationDialog();
      },
    });
  };

  useEffect(() => {
    return () => {
      images.forEach((img) => URL.revokeObjectURL(img.preview));
    };
  }, []);

  const handleSave = async () => {
    if (!selectedEventId) {
      toast.error("Please select an event before uploading.");
      return;
    }
    if (images.length === 0) {
      toast.error("Please add at least one image or video.");
      return;
    }

    setUploading(true);
    setCurrentUploadIndex(0);
    let successCount = 0;
    let failCount = 0;

    try {
      const organizerData = localStorage.getItem("organizerData");
      const organizerId = organizerData ? JSON.parse(organizerData).organizerId : null;

      for (let i = 0; i < images.length; i++) {
        setCurrentUploadIndex(i + 1);
        const img = images[i];
        try {
          const formData = new FormData();
          if (organizerId) formData.append("createdBy", String(organizerId));
          formData.append("file", img.file);
          if (img.caption.trim()) formData.append("eventName", img.caption.trim());
          formData.append("referenceId", String(selectedEventId));
          formData.append("referenceType", "Event");
          const response = await createDiscover(formData);
          if (response?.id) {
            successCount++;
          } else {
            failCount++;
          }
        } catch {
          failCount++;
        }
      }

      if (successCount > 0 && failCount === 0) {
        toast.success(`${successCount} moment${successCount > 1 ? "s" : ""} uploaded successfully!`);
        setImages((prev) => {
          prev.forEach((img) => URL.revokeObjectURL(img.preview));
          return [];
        });
        fetchDiscovers(selectedEventId as number, 1);
      } else if (successCount > 0 && failCount > 0) {
        toast.warning(`${successCount} uploaded, ${failCount} failed.`);
        setImages((prev) => {
          prev.slice(0, successCount).forEach((img) => URL.revokeObjectURL(img.preview));
          return prev.slice(successCount);
        });
        fetchDiscovers(selectedEventId as number, 1);
      } else {
        toast.error("Upload failed. Please try again.");
      }
    } catch (e) {
      toast.error("An unexpected error occurred.");
    } finally {
      setUploading(false);
      setCurrentUploadIndex(0);
    }
  };

  const selectedEventObj = events.find((e) => e.eventId === selectedEventId);
  const eventSelectOptions = events.map((ev) => ({
    label: ev.eventName,
    value: String(ev.eventId),
  }));

  return (
    <div className={`discover-container ${isNightMode ? "text-white" : "text-dark"}`}>
      <Background />
      <ToastContainer autoClose={2500} position="top-right" theme={isNightMode ? "dark" : "light"} />

      {/* Page Header */}
      <PageHeader
        title="Discover Moments"
        subtitle="Upload, curate, and feature high-resolution photos and video highlights for your events."
        icon={Compass}
        isLoading={eventsLoading}
        actions={
          images.length > 0 ? (
            <button
              type="button"
              className="v3-btn-primary"
              onClick={handleSave}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <PulseLoader color="#ffffff" size={6} />
                  <span>Uploading {currentUploadIndex}/{images.length}...</span>
                </>
              ) : (
                <>
                  <UploadCloud size={16} />
                  <span>Upload ({images.length}) Moments</span>
                </>
              )}
            </button>
          ) : undefined
        }
      />

      {/* Main Glass Panel Card */}
      <div className="glass-panel mb-4">
        
        {/* Step 1: Target Event Selector using CustomSelect */}
        <div className="mb-4">
          <label className="v3-label d-flex align-items-center gap-2 mb-2">
            <Calendar size={14} className="text-pink" /> Target Event *
          </label>
          <CustomSelect
            options={eventSelectOptions}
            value={selectedEventId !== "" ? String(selectedEventId) : ""}
            onChange={handleSelectEvent}
            placeholder={eventsLoading ? "Loading events registry..." : "— Select an event to showcase —"}
          />
          {events.length === 0 && !eventsLoading && (
            <small className="contact-sub-text fs-12px mt-1 d-block">
              No active events found for your organizer account.
            </small>
          )}
        </div>

        {/* Step 2: Drag & Drop Dropzone */}
        <div
          {...getRootProps()}
          className={`discover-dropzone mb-4 ${isDragActive ? "drag-active" : ""}`}
        >
          <input {...getInputProps()} />
          <div className="discover-dropzone-icon">
            <UploadCloud size={24} />
          </div>
          <div className="text-center">
            <div className="fw-bold fs-14px contact-name-text mb-1">
              {isDragActive ? "Drop media files here..." : "Drag & drop photos or videos here"}
            </div>
            <p className="contact-sub-text fs-12px mb-0">
              Supports JPEG, PNG, WEBP, GIF images and MP4, MOV video highlights
            </p>
          </div>
        </div>

        {/* Queue Items Grid */}
        {images.length > 0 && (
          <div className="mb-4 pt-3 border-top border-white-10">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span className="v3-label mb-0 d-flex align-items-center gap-2">
                <Clock size={14} className="text-orange" /> Upload Queue ({images.length} item{images.length > 1 ? "s" : ""})
              </span>
              <button
                type="button"
                className="v3-btn-ghost text-pink"
                onClick={() => {
                  images.forEach((img) => URL.revokeObjectURL(img.preview));
                  setImages([]);
                }}
              >
                Clear Queue
              </button>
            </div>

            <div className="discover-grid mb-3">
              {images.map((img, index) => {
                const isVideo = img.file.type.startsWith("video/");

                return (
                  <div className="discover-grid-item" key={index}>
                    <div className="discover-card">
                      <div className="discover-media-wrap">
                        {isVideo ? (
                          <video src={img.preview} />
                        ) : (
                          <img src={img.preview} alt={`Moment ${index + 1}`} />
                        )}
                        <span className="discover-card-badge">
                          {isVideo ? <Film size={12} /> : <ImageIcon size={12} />}
                          {isVideo ? "VIDEO" : "PHOTO"}
                        </span>
                        <button
                          type="button"
                          className="discover-card-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImage(index);
                          }}
                          aria-label="Remove image"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                      <div className="discover-card-caption">
                        <input
                          type="text"
                          className="v3-input"
                          placeholder="Add caption (optional)..."
                          value={img.caption}
                          onChange={(e) => updateCaption(index, e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>

      {/* Existing Showcase Moments Section */}
      {selectedEventId !== "" && (
        <div className="glass-panel">
          <div className="d-flex align-items-center justify-content-between pb-3 mb-4 border-bottom border-white-10">
            <h3 className="v3-section-head mb-0 border-0 p-0">
              <Sparkles size={18} className="text-pink" />
              Existing Moments Showcase {selectedEventObj ? `— ${selectedEventObj.eventName}` : ""}
            </h3>
            {totalCount > 0 && (
              <span className="contact-action-btn py-1 px-3 fs-12px font-mono">
                {totalCount} moment{totalCount > 1 ? "s" : ""}
              </span>
            )}
          </div>

          {previewLoading ? (
            <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-3">
              <PulseLoader color="#EE3F7C" size={12} />
              <span className="fs-12px font-mono contact-sub-text">Fetching event moments...</span>
            </div>
          ) : existingItems.length === 0 ? (
            <div className="v3-empty-state py-5">
              <ImageIcon size={32} className="contact-sub-text mb-2" />
              <div className="fw-bold contact-name-text fs-14px">No Discover Moments Yet</div>
              <p className="contact-sub-text fs-12px mb-0 mt-1">
                Drag and drop media above to publish the first discover moments for this event.
              </p>
            </div>
          ) : (
            <div className="discover-grid">
              {existingItems.map((item) => {
                const isVideo =
                  item.fileType?.startsWith("video") ||
                  Boolean(item.fileUrl?.match(/\.(mp4|mov|webm)$/i));

                return (
                  <div className="discover-grid-item" key={item.id}>
                    <div className="discover-card">
                      <div className="discover-media-wrap">
                        {isVideo ? (
                          <video src={item.fileUrl} controls />
                        ) : (
                          <img
                            src={item.eventImage || item.fileUrl}
                            alt={item.eventName || "Discover moment"}
                          />
                        )}
                        <span className="discover-card-badge">
                          {isVideo ? <Film size={12} /> : <ImageIcon size={12} />}
                          {isVideo ? "VIDEO" : "PHOTO"}
                        </span>
                        <button
                          type="button"
                          className="discover-card-delete"
                          onClick={() => removeExistingItem(item)}
                          aria-label="Delete item"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                      {item.eventName && (
                        <div className="discover-card-caption">
                          <input
                            type="text"
                            className="v3-input opacity-75"
                            value={item.eventName}
                            disabled
                            readOnly
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div ref={sentinelRef} style={{ height: 1 }} />

          {loadingMore && (
            <div className="d-flex align-items-center justify-content-center gap-2 py-4">
              <PulseLoader color="#EE3F7C" size={8} />
              <span className="fs-12px font-mono contact-sub-text">Loading more moments...</span>
            </div>
          )}
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        toggle={closeConfirmationDialog}
        onConfirm={confirmationDialog.onConfirm}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        type={confirmationDialog.type}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        icon="fa-solid fa-exclamation-triangle"
      />
    </div>
  );
};

export default DiscoverPage;